import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * The safety net: when something on a page crashes, React would otherwise leave a blank screen. This shows what went
 * wrong and the way back instead. Around each hub page (the header and tabs keep working, and moving to another page
 * clears it) and around the whole site (for a crash in the header itself).
 */
export default class ErrorBoundary extends Component<{ children: ReactNode; whole?: boolean }, { error?: Error }> {
  state: { error?: Error } = {};

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('D2 Companion: a page crashed', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    // a newer version of the site was put live while this one was open: its page files are gone, so a reload fixes it
    const stale = /dynamically imported module|Failed to fetch|Loading chunk|Importing a module script failed/i.test(error.message);
    return (
      <div className={`g-crash ${this.props.whole ? 'whole' : ''}`} role="alert">
        <h2>{stale ? 'The site was updated' : this.props.whole ? 'Something went wrong' : 'This page ran into a problem'}</h2>
        <p className="g-text2">
          {stale ? 'A newer version went live while this page was open. Reload to get it.'
            : this.props.whole ? 'The site stopped working. Reloading usually fixes it.'
              : 'The rest of the hub still works: pick another tab or page, or try this one again.'}
        </p>
        {!stale && <pre>{error.message}</pre>}
        <div className="g-crash-buttons">
          {!this.props.whole && !stale && <button type="button" className="btn primary" onClick={() => this.setState({ error: undefined })}>Try again</button>}
          <button type="button" className={`btn ${this.props.whole || stale ? 'primary' : ''}`} onClick={() => location.reload()}>Reload the site</button>
        </div>
      </div>
    );
  }
}
